<script lang="ts" setup>
import vSelect from "vue-select";
import axiosdb from "@/axios-db";
import axiosaux from "@/axios-aux";
import dateFormat from "dateformat";
import { jsonToGraphQLQuery, EnumType } from "json-to-graphql-query";
import LoadingModal from "@/shared/LoadingModal.vue";
import ErrorModal from "@/shared/ErrorModal.vue";
import { vMaska } from "maska/vue";
import lodash from "lodash";
import {
  BAlert,
  BButton,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormSelect,
  BFormTextarea,
  BRow,
  BTooltip,
} from "bootstrap-vue-next";
import vueRecaptcha from "vue3-recaptcha2";

interface DateRange {
  start: Date;
  end: Date;
}
interface CourseInfo {
  courseId: number;
  requestedStartDate: Date;
  isWaitlist: boolean;
}

//Validators for vuelidate
import { required, requiredIf, email, helpers } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { computed, onMounted, reactive, ref, toRefs, watchEffect } from "vue";
import { useToast } from "vue-toast-notification";
import { DateParts } from "v-calendar/dist/types/src/utils/date/helpers";
import { CalendarDay } from "v-calendar/dist/types/src/utils/page";

const toast = useToast();

const postalCodeValidator = (value: any) =>
  !helpers.req(value) || /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(value);
const phoneValidator = (value: any) =>
  !helpers.req(value) || /^[1-9]\d{2}-\d{3}-\d{4}/.test(value);
const mustBeTrue = (value: boolean) => value;

const showLoading = ref<boolean>(false);
const showError = ref<boolean>(false);
const disabledDates = ref<any>([]);
const invisibleRecaptcha = ref();

const state = reactive<any>({
  submitted: false,
  // temproary write access token
  guestToken: null,
  authGuestToken: null,
  // Get field values from the API
  studentTypes: [],
  aboriginalities: [],
  countries: [],
  residentTypes: [],
  languages: [],
  religions: [],
  schools: [],
  schoolDivisions: [],
  schoolYears: [],
  educationPartners: [],
  requirements: null,
  // The selected options on the form
  studentType: null,
  firstname: null,
  middlename: null,
  lastname: null,
  gender: null,
  street: null,
  city: null,
  province: null,
  postalcode: null,
  phone: null,
  dob: null,
  yog: null,
  aboriginality: null,
  citizenship1: null,
  citizenship2: null,
  residentType: null,
  language1: null,
  language2: null,
  cob: null,
  coo: null,
  studentReligion: null,
  motherReligion: null,
  fatherReligion: null,
  studentCell: null,
  studentEmail: null,
  studentEmail2: null,
  ministryNo: null,
  parentFirstName: null,
  parentLastName: null,
  parentEmail: null,
  parentEmail2: null,
  parentPhone: null,
  schoolDivision: null,
  schoolDivisionOther: null,
  school: null,
  schoolOther: null,
  schoolContactFirstName: null,
  schoolContactLastName: null,
  schoolContactEmail: null,
  attendingOtherSchoolDivision: null,
  lastAttendedSchoolDivision: null,
  lastAttendedSchoolDivisionOther: null,
  lastAttendedSchool: null,
  lastAttendedSchoolOther: null,
  lastAttendedDate: null,
  partnerInEducation: null,
  notes: null,
  eligibilityGuidelines: false,
  prereqs: false,
  accurate: false,
  recordsShared: false,
  recaptchaResponse: null,
  // The courses pulled out of the api
  courses: [],
  selectedCourses: [],
  loadRecaptcha: false,
  buttonEnabled: true,
});

const { selectedCourses } = toRefs(state);
const confirmEmail = (value: string, other: string) => value === other;

const validations = {
  studentType: {
    required,
  },
  firstname: {
    required,
  },
  middlename: {},
  lastname: {
    required,
  },
  gender: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_additional_sis_data
      );
    }),
  },
  street: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_additional_sis_data
      );
    }),
  },
  city: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_additional_sis_data
      );
    }),
  },
  province: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_additional_sis_data
      );
    }),
  },
  postalcode: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_additional_sis_data
      );
    }),
    postalCodeValidator,
  },
  phone: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_additional_sis_data
      );
    }),
    phoneValidator,
  },
  dob: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_additional_sis_data
      );
    }),
  },
  yog: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_yog
      );
    }),
  },
  aboriginality: {},
  citizenship1: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_citizenship
      );
    }),
  },
  citizenship2: {},
  residentType: {
    required: requiredIf(function () {
      return state.citizenship1 && state.citizenship1.label != "Canada";
    }),
  },
  language1: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_language
      );
    }),
  },
  language2: {},
  cob: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_origins
      );
    }),
  },
  coo: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_origins
      );
    }),
  },
  studentReligion: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_religion
      );
    }),
  },
  motherReligion: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_religion
      );
    }),
  },
  fatherReligion: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_religion
      );
    }),
  },
  studentCell: {
    phoneValidator,
  },
  studentEmail: {
    required,
    email,
    confirmEmail: !confirmEmail(state.studentEmail, state.parentEmail),
  },
  studentEmail2: {
    required,
    email,
  },
  ministryNo: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_additional_sis_data
      );
    }),
  },
  parentFirstName: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_parent
      );
    }),
  },
  parentLastName: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_parent
      );
    }),
  },
  parentEmail: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_parent
      );
    }),
    email,
    confirmEmail: !confirmEmail(state.studentEmail, state.parentEmail),
  },
  parentEmail2: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_parent
      );
    }),
    confirmEmail: confirmEmail(state.parentEmail2, state.parentEmail),
  },
  parentPhone: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_parent
      );
    }),
    phoneValidator,
  },
  schoolDivision: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_school_division
      );
    }),
  },
  schoolDivisionOther: {
    required: requiredIf(function () {
      return state.schoolDivision == "Other";
    }),
  },
  school: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_school
      );
    }),
  },
  schoolOther: {
    required: requiredIf(function () {
      return state.school == "Other";
    }),
  },
  schoolContactFirstName: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_school_contact
      );
    }),
  },
  schoolContactLastName: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_school_contact
      );
    }),
  },
  schoolContactEmail: {
    email,
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_school_contact
      );
    }),
  },
  attendingOtherSchoolDivision: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_attending_other_school_div
      );
    }),
  },
  lastAttendedSchoolDivision: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_attending_other_school_div &&
        state.attendingOtherSchoolDivision == "false"
      );
    }),
  },
  lastAttendedSchoolDivisionOther: {
    required: requiredIf(function () {
      return state.lastAttendedSchoolDivision == "Other";
    }),
  },
  lastAttendedSchool: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_attending_other_school_div &&
        state.attendingOtherSchoolDivision == "false"
      );
    }),
  },
  lastAttendedSchoolOther: {
    required: requiredIf(function () {
      return state.lastAttendedSchool == "Other";
    }),
  },
  lastAttendedDate: {
    required: requiredIf(function () {
      return (
        state.studentType &&
        state.studentTypes.filter((a) => a.id == state.studentType)[0]
          .require_attending_other_school_div &&
        state.attendingOtherSchoolDivision == "false"
      );
    }),
  },
  partnerInEducation: {},
  eligibilityGuidelines: {
    mustBeTrue,
  },
  prereqs: {
    mustBeTrue,
  },
  accurate: {
    mustBeTrue,
  },
  recordsShared: {
    mustBeTrue,
  },
  notes: {
    required,
  },
};

const $v = useVuelidate(validations, state);

function toGQL(queryObject: any, variables = {}) {
  // Using the jsonToGraphQuery library to convert the json defined query into a graphQL query
  return { query: jsonToGraphQLQuery(queryObject), variables: variables };
}

async function loadFormData() {
  showLoading.value = true;

  let query = {
    query: {
      halo_student_type: {
        __args: {
          order_by: { sort_order: new EnumType("desc") },
        },
        id: true,
        name: true,
        require_additional_sis_data: true,
        require_attending_other_school_div: true,
        require_citizenship: true,
        require_language: true,
        require_origins: true,
        require_parent: true,
        require_religion: true,
        require_school: true,
        require_school_contact: true,
        require_school_division: true,
        require_yog: true,
        require_partner_in_education: true,
      },
      halo_aboriginality: {
        id: true,
        name: true,
      },
      halo_country: {
        id: true,
        name: true,
      },
      halo_resident_type: {
        id: true,
        name: true,
      },
      halo_language: {
        id: true,
        name: true,
      },
      halo_religion: {
        id: true,
        name: true,
      },
      halo_school: {
        __args: {
          order_by: { name: new EnumType("asc") },
        },
        id: true,
        name: true,
      },
      halo_school_division: {
        __args: {
          order_by: { name: new EnumType("asc") },
        },
        id: true,
        name: true,
      },
      halo_education_partner: {
        __args: {
          order_by: { name: new EnumType("asc") },
        },
        id: true,
        name: true,
      },
      halo_course: {
        __args: {
          order_by: { name: new EnumType("asc") },
        },
        id: true,
        name: true,
        length: true,
      },
      halo_school_year: {
        name: true,
        start_date: true,
        end_date: true,
        summer_start_date: true,
        summer_end_date: true,
      },
    },
  };

  try {
    let res = await axiosdb.post(
      "/graphql?t=" + new Date().valueOf(),
      toGQL(query)
    );
    state.studentTypes = res.data.data.halo_student_type;
    state.aboriginalities = res.data.data.halo_aboriginality.map((a: any) => {
      return { text: a.name, value: a.id };
    });
    state.countries = res.data.data.halo_country.map((a: any) => {
      return { label: a.name, value: a.id };
    });
    state.residentTypes = res.data.data.halo_resident_type.map((a: any) => {
      return { label: a.name, value: a.id };
    });
    state.languages = res.data.data.halo_language.map((a: any) => {
      return { label: a.name, value: a.id };
    });
    state.religions = res.data.data.halo_religion.map((a: any) => {
      return { label: a.name, value: a.id };
    });
    state.schools = res.data.data.halo_school.map((a: any) => {
      return a.name;
    });
    state.schoolDivisions = res.data.data.halo_school_division.map((a: any) => {
      return a.name;
    });
    state.courses = res.data.data.halo_course.map((a: any) => {
      return { label: a.name, value: a.id, length: a.length };
    });
    state.schoolYears = res.data.data.halo_school_year;
    state.educationPartners = res.data.data.halo_education_partner.map(
      (a: any) => {
        return { text: a.name, value: a.id };
      }
    );
    showLoading.value = false;
  } catch (err) {
    console.log(err);
    showLoading.value = false;

    showError.value = true;
  }
}

async function changeCourseInput(index: number) {
  // the index being passed here is the index of this particular dropdown in the state.selectedCourses array
  // If the course drop down is changed we clear all the variables
  state.selectedCourses[index].availableStartDates = [];
  state.selectedCourses[index].calendarAttrs = [];
  state.selectedCourses[index].startDate = null;

  // If, after the change, there is still a course, then we should get that information
  if (state.selectedCourses[index].course) {
    // Load the available dates from the API for the selected course
    showLoading.value = true;

    let query = {
      query: {
        halo_course_availability: {
          __args: {
            course_id: state.selectedCourses[index].course.value,
          },
          full_dates: true,
          open_dates: true,
        },
      },
    };

    await axiosdb
      .post("/graphql?t=" + new Date().valueOf(), toGQL(query))
      .then((result) => {
        showLoading.value = false;
        state.selectedCourses[index].availableStartDates = lodash.union(
          result.data.data.halo_course_availability.open_dates,
          result.data.data.halo_course_availability.full_dates
        );
        state.selectedCourses[index].openDates =
          result.data.data.halo_course_availability.open_dates;
        state.selectedCourses[index].fullDates =
          result.data.data.halo_course_availability.full_dates;

        disabledDates.value = [
          {
            start: null,
            end: null,
            on: [
              (days) =>
                !result.data.data.halo_course_availability.open_dates.includes(
                  days.date
                ),
            ],
          },
        ];

        state.selectedCourses[index].calendarAttrs.push({
          key: "open",
          dates: buildDateObject(
            result.data.data.halo_course_availability.open_dates
          ),
          bar: "green",
          content: {
            style: {
              color: "black",
            },
          },
          popover: {
            label: "This date is available",
          },
        });
        state.selectedCourses[index].calendarAttrs.push({
          key: "full",
          dates: buildDateObject(
            result.data.data.halo_course_availability.full_dates
          ),
          bar: "yellow",
          content: {
            style: {
              color: "black",
            },
          },
          popover: {
            label: "This date is full, you will be added to the waitlist",
          },
        });
      })
      .catch((err) => {
        console.log("err", err);
        showLoading.value = false;
        showError.value = true;
        toast.error("Error occurred retrieving availability of courses");
      });
  }
}

function onlyEnableAllowedDates(
  component: number,
  dateParts: DateParts,
  index: number
) {
  return state.selectedCourses[index].openDates.includes(dateParts.date);
}
function removeSelectedCourse(index: number) {
  state.selectedCourses.splice(index, 1);
}

function pickDate(calendarDay: CalendarDay, courseIndex: number) {
  const currentCourse = state.selectedCourses[courseIndex];

  if (
    !currentCourse.openDates.some(
      (date) => new Date(date).getTime() == calendarDay.date.getTime()
    ) && !currentCourse.fullDates.some(
      (date) => new Date(date).getTime() == calendarDay.date.getTime()
    )
  ) {
    state.selectedCourses[courseIndex].startDate = null;
  }
  // Remove the existing span from the calendarAttrs array if it exists.  we don't want two spans on the calendar
  let spanIndex = state.selectedCourses[courseIndex].calendarAttrs.findIndex(
    (a: any) => a.key == "selectedSpan"
  );
  if (spanIndex >= 0) {
    state.selectedCourses[courseIndex].calendarAttrs.splice(spanIndex, 1);
  }
  // If the date was DESELECTED then we don't need to continue, we don't have a date to work with
  if (!state.selectedCourses[courseIndex].startDate) {
    return;
  }

  // We have picked a date, now we find out if that date falls within a standard September-June school year
  // If schoolYear is defined after this find it will be the school year with the start and end dates.  If it isn't then the
  // date picked is a summer class and we shouldn't have to worry about suspensions.  If the span created by the date
  // selected goes past the end of the semester that is returned then we have to account for that.

  let schoolYear: any = state.schoolYears.find(
    (sy: any) =>
      new Date(sy.start_date) <=
        new Date(state.selectedCourses[courseIndex].startDate) &&
      new Date(sy.end_date) >=
        new Date(state.selectedCourses[courseIndex].startDate)
  );
  let spans: DateRange[] = [];

  // Check to see if the start date plus the length goes beyond the last day of the semester.
  if (
    schoolYear &&
    addDays(
      state.selectedCourses[courseIndex].startDate,
      state.selectedCourses[courseIndex].course.length - 1
    ) > new Date(schoolYear.end_date)
  ) {
    // If the end date of the enrollment goes past the end date of the semester then we only make the span until the end of the semester
    spans.push({
      start: state.selectedCourses[courseIndex].startDate,
      end: new Date(schoolYear.end_date),
    });
    // To show this information to the user on the screen we do some calculations
    state.selectedCourses[courseIndex].willBeSuspended = true;
    state.selectedCourses[courseIndex].semesterEndDate = new Date(
      schoolYear.end_date
    );
    state.selectedCourses[courseIndex].amountCompleteBeforeSuspension =
      dateDiffInDays(
        state.selectedCourses[courseIndex].startDate,
        new Date(schoolYear.end_date)
      ) + 1;
  } else {
    // If the end date of the enrollment is within the semester end date then we just make the span for the full length
    spans.push({
      start: state.selectedCourses[courseIndex].startDate,
      end: addDays(
        state.selectedCourses[courseIndex].startDate,
        state.selectedCourses[courseIndex].course.length - 1
      ),
    });
    state.selectedCourses[courseIndex].willBeSuspended = false;
    state.selectedCourses[courseIndex].amountCompleteBeforeSuspension = null;
  }
  // If the date was selected then we create the span to display on the calendar
  state.selectedCourses[courseIndex].calendarAttrs.push({
    key: "selectedSpan",
    highlight: {
      color: "purple",
    },
    dates: spans,
  });
}

function buildDateObject(dates: any[]) {
  let dArray: DateRange[] = [];
  for (let d of dates) {
    dArray.push({ start: d, end: d });
  }
  // If there is nothing then the only available date is one day in 1969... if this is a blank array
  // then all dates become available.
  if (dArray.length == 0) {
    dArray.push({ start: new Date(1 * 1000), end: new Date(1 * 1000) });
  }
  return dArray;
}

async function verifyData() {

  console.log(state.selectedCourses)
  // check all the validator fields to make sure they are filled in
  $v.value.$touch();
  if (state.parentEmail == state.studentEmail) {
    toast.error(
      "Your student email cannot be the same as your parent's email!"
    );
    return;
  }

  if (!(await $v.value.$validate())) {
    console.log($v.value.$errors);
    toast.error(
      "Some fields incomplete or invalid, please go back and correct this"
    );
    return;
  }
  // do extra checks to ensure the student has picked courses
  if (state.selectedCourses.length == 0) {
    toast.error("Please add at least one course");
    return;
  }

  for (let c of state.selectedCourses) {
    if (!c.course) {
      toast.error(
        "Please select a course from the drop down or remove the course"
      );
      return;
    }
    if (!c.startDate) {
      toast.error("Please select a valid start date for " + c.course.label);
      return;
    }
  }

  state.buttonEnabled = false;
  invisibleRecaptcha.value.execute();
}

function recaptchaCallback(recaptchaToken: string) {
  axiosaux
    .post("/auth/verify-captcha", { recaptchaToken: recaptchaToken })
    .then((result) => {
      showLoading.value = true;
      sendRegistration();
    })
    .catch(() => {
      toast.error("Error occured during submission");
    });
}

function onExpired() {
  invisibleRecaptcha.value.reset();
}

async function sendRegistration() {
  let mutation: any = {
    mutation: {
      halo_student_register: {
        __args: {
          student_type_id: state.studentType,
          first_name: state.firstname,
          middle_name: state.middlename,
          last_name: state.lastname,
          gender: state.gender,
          phone: state.phone,
          dob: state.dob ? state.dob.toString() : null,
          yog: state.yog,
          aboriginality_id: state.aboriginality,
          citizenship1_id: state.citizenship1 ? state.citizenship1.value : null,
          citizenship2_id: state.citizenship2 ? state.citizenship2.value : null,
          resident_type_id: state.residentType
            ? state.residentType.value
            : null,
          language1_id: state.language1 ? state.language1.value : null,
          language2_id: state.language2 ? state.language2.value : null,
          birth_country_id: state.cob ? state.cob.value : null,
          origin_country_id: state.coo ? state.coo.value : null,
          student_religion_id: state.studentReligion
            ? state.studentReligion.value
            : null,
          mother_religion_id: state.motherReligion
            ? state.motherReligion.value
            : null,
          father_religion_id: state.fatherReligion
            ? state.fatherReligion.value
            : null,
          cell: state.studentCell,
          email: state.studentEmail,
          ministry_id: state.ministryNo,
          school_division: state.schoolDivisionOther
            ? state.schoolDivisionOther
            : state.schoolDivision,
          school: state.schoolOther ? state.schoolOther : state.school,
          attending_other_school_division:
            state.attendingOtherSchoolDivision == "true" ? true : false,
          last_attended_school_division: state.lastAttendedSchoolDivisionOther
            ? state.lastAttendedSchoolDivisionOther
            : state.lastAttendedSchoolDivision,
          last_attended_school: state.lastAttendedSchoolOther
            ? state.lastAttendedSchoolOther
            : state.lastAttendedSchool,
          last_attended_date: state.lastAttendedDate
            ? state.lastAttendedDate.toString()
            : null,
          education_partner_id: state.partnerInEducation,
          notes: state.notes,
          agreed_to_eligibility_guidelines: state.eligibilityGuidelines,
          agreed_to_prereqs_complete: state.prereqs,
          agreed_to_information_accurate: state.accurate,
          agreed_to_information_shared: state.recordsShared,
          street: state.street || null,
          city: state.city || null,
          province: state.province || null,
          postal_code: state.postalcode || null,
        },
        result: true,
      },
    },
  };

  if (
    state.studentType &&
    state.studentTypes.filter((a: any) => a.id == state.studentType)[0]
      .require_parent
  ) {
    mutation.mutation.halo_student_register.__args._parents = [
      {
        firstname: state.parentFirstName,
        lastname: state.parentLastName,
        email: state.parentEmail,
        phone: state.parentPhone,
      },
    ];
  }

  if (
    state.studentType &&
    state.studentTypes.filter((a: any) => a.id == state.studentType)[0]
      .require_school_contact
  ) {
    mutation.mutation.halo_student_register.__args._school_contacts = [
      {
        firstname: state.schoolContactFirstName,
        lastname: state.schoolContactLastName,
        email: state.schoolContactEmail,
      },
    ];
  }

  let courses: CourseInfo[] = [];

  for (let selectedCourse of state.selectedCourses) {
    if (
      selectedCourse.fullDates.filter(
        (a: any) =>
          new Date(a).toString() ==
          new Date(selectedCourse.startDate).toString()
      ).length > 0
    ) {
      courses.push({
        courseId: selectedCourse.course.value,
        requestedStartDate: selectedCourse.startDate.toString(),
        isWaitlist: true,
      });
    } else {
      courses.push({
        courseId: selectedCourse.course.value,
        requestedStartDate: selectedCourse.startDate.toString(),
        isWaitlist: false,
      });
    }
  }

  mutation.mutation.halo_student_register.__args.courses = courses;

  try {
    let res = await axiosdb.post(
      "/graphql?t=" + new Date().valueOf(),
      toGQL(mutation)
    );
    if (res.data.data.halo_student_register.result === true) {
      state.submitted = true;
    } else {
      toast.error("An error has occured during registration");
    }
    if (res.data.errors) {
      toast.error("An error has occured during registration");
    }
  } catch (err) {
    toast.error("An error has occured during registration");
  }

  showLoading.value = false;
}

function resetForm() {
  (state.submitted = false),
    (state.guestToken = null),
    (state.authGuestToken = null),
    (state.requirements = null),
    (state.studentType = null),
    (state.firstname = null),
    (state.middlename = null),
    (state.lastname = null),
    (state.gender = null),
    (state.street = null),
    (state.city = null),
    (state.province = null),
    (state.postalcode = null),
    (state.phone = null),
    (state.dob = null),
    (state.aboriginality = null),
    (state.citizenship1 = null),
    (state.citizenship2 = null),
    (state.residentType = null),
    (state.language1 = null),
    (state.language2 = null),
    (state.cob = null),
    (state.coo = null),
    (state.studentReligion = null),
    (state.motherReligion = null),
    (state.fatherReligion = null),
    (state.studentCell = null),
    (state.studentEmail = null),
    (state.studentEmail2 = null),
    (state.ministryNo = null),
    (state.parentFirstName = null),
    (state.parentLastName = null),
    (state.parentEmail = null),
    (state.parentEmail2 = null),
    (state.parentPhone = null),
    (state.schoolDivision = null),
    (state.schoolDivisionOther = null),
    (state.school = null),
    (state.schoolOther = null),
    (state.schoolContactFirstName = null),
    (state.schoolContactLastName = null),
    (state.schoolContactEmail = null),
    (state.attendingOtherSchoolDivision = null),
    (state.partnerInEducation = null),
    (state.notes = null),
    (state.eligibilityGuidelines = false),
    (state.prereqs = false),
    (state.accurate = false),
    (state.recordsShared = false),
    (state.recaptchaResponse = null);
  state.selectedCourses = [];
  $v.value.$reset();
}

function validityColor(data: any) {
  if ($v.value[data].$error) return "is-invalid";
  else if (
    $v.value[data].$dirty &&
    !$v.value[data].$invalid &&
    $v.value[data].$model
  )
    return "is-valid";

  return "";
}

function addDays(date: any, days: number) {
  var result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function dateDiffInDays(a: Date, b: Date) {
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
}

const selectableCourses = computed(() => {
  let alreadySelected = state.selectedCourses.map((a) => a.course);
  return lodash.difference(state.courses, alreadySelected);
});

onMounted(() => {
  setTimeout(() => (state.loadRecaptcha = true), 2000);
  setTimeout(() => loadFormData(), 1000);
});
</script>
<template>
  <div class="animated fadeIn background-color">
    <div class="row">
      <div
        class="card border-top-2 border-top-info col-md-8 offset-md-2 text-left"
      >
        <div class="card-body">
          <div class="text-center">
            <img
              class="navbar-brand-full"
              src="img/logo.png"
              height="100"
              alt="LO Logo"
            />
            <h2>Learning Online Registration</h2>
          </div>
          <ul class="step d-flex flex-nowrap pt-4">
            <li class="step-item active">
              <span>Step 1<br />Student registers</span>
            </li>
            <li class="step-item">
              <span
                >Step 2<br />Student & parent/guardian agree to appropriate
                policy documents</span
              >
            </li>
            <li class="step-item">
              <span
                >Step 3<br />Student enrollments will be reviewed and
                approved</span
              >
            </li>
            <li class="step-item">
              <span>Step 4<br />Student will be enrolled into classes</span>
            </li>
          </ul>
          <br />
          <div v-if="!state.submitted">
            <div class="row mt-4">
              <div
                class="col"
                :class="{ required: $v.studentType.hasOwnProperty('required') }"
              >
                <label>Student Type</label>
                <BFormSelect
                  v-model="$v.studentType.$model"
                  :options="
                    state.studentTypes?.map((a) => {
                      return { text: a.name, value: a.id };
                    })
                  "
                  :class="validityColor('studentType')"
                ></BFormSelect>
                <div class="invalid-tooltip">This field is required</div>
              </div>
            </div>

            <div v-if="state.studentType === 6" class="row mx-2">
              <BAlert class="mt-4" variant="warning" :model-value="true"
                >In the <u>Additional Information</u> section at the bottom of
                this form, please provide a brief explanation as to why your
                child is registering with Learning Online. All registrations
                will be reviewed by program administration prior to
                approval.</BAlert
              >
            </div>
            <div class="row mt-3">
              <div
                class="form-group col"
                :class="{ required: $v.firstname.hasOwnProperty('required') }"
              >
                <label>First Name</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="$v.firstname.$model"
                  :class="validityColor('firstname')"
                  name="firstname"
                />
                <div class="invalid-tooltip">This field is required</div>
              </div>

              <div
                class="form-group col"
                :class="{ required: $v.middlename.hasOwnProperty('required') }"
              >
                <label>Middle Name</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="$v.middlename.$model"
                  :class="validityColor('middlename')"
                  name="middlename"
                />
              </div>

              <div
                class="form-group col"
                :class="{ required: $v.lastname.hasOwnProperty('required') }"
              >
                <label>Last Name</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="$v.lastname.$model"
                  :class="validityColor('lastname')"
                  name="lastname"
                />
                <div class="invalid-tooltip">This field is required</div>
              </div>
            </div>

            <div
              class="row mt-3"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_additional_sis_data
              "
            >
              <div
                class="form-group col"
                :class="{ required: $v.gender.hasOwnProperty('required') }"
              >
                <label>Gender</label>
                <BFormSelect
                  v-model="$v.gender.$model"
                  :options="['Male', 'Female', 'Other/Unspecified']"
                  :class="validityColor('gender')"
                ></BFormSelect>
                <div class="invalid-tooltip">This field is required</div>
              </div>
            </div>
            <div
              class="row"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_additional_sis_data
              "
            >
              <div
                class="form-group col"
                :class="{ required: $v.street.hasOwnProperty('required') }"
              >
                <label>Number & Street/PO Box</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="$v.street.$model"
                  :class="validityColor('street')"
                  name="address1"
                />
                <div class="invalid-tooltip">This field is required</div>
              </div>
            </div>
            <div
              class="row mt-3"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_additional_sis_data
              "
            >
              <div
                class="form-group col"
                :class="{ required: $v.city.hasOwnProperty('required') }"
              >
                <label>City</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="$v.city.$model"
                  :class="validityColor('city')"
                  name="city"
                />
                <div class="invalid-tooltip">This field is required</div>
              </div>
              <div
                class="form-group col"
                :class="{ required: $v.province.hasOwnProperty('required') }"
              >
                <label>Province</label>
                <BFormSelect
                  v-model="$v.province.$model"
                  :options="[
                    { text: 'Alberta', value: 'AB' },
                    { text: 'British Columbia', value: 'BC' },
                    { text: 'Manitoba', value: 'MB' },
                    { text: 'New Brunswick', value: 'NB' },
                    { text: 'Newfoundland and Labrador', value: 'NL' },
                    { text: 'Nova Scotia', value: 'NS' },
                    { text: 'Northwest Territories', value: 'NT' },
                    { text: 'Nunavut', value: 'NU' },
                    { text: 'Ontario', value: 'ON' },
                    { text: 'Prince Edward Island', value: 'PE' },
                    { text: 'Quebec', value: 'QC' },
                    { text: 'Saskatchewan', value: 'SK' },
                    { text: 'Yukon', value: 'YT' },
                  ]"
                  :class="validityColor('province')"
                ></BFormSelect>
                <div class="invalid-tooltip">This field is required</div>
              </div>
              <div
                class="form-group col"
                :class="{
                  required: $v.postalcode.hasOwnProperty('required'),
                }"
              >
                <label>Postal Code</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="state.postalcode"
                  @blur="$v.postalcode.$touch()"
                  data-maska-tokens="{ 'Z' : { 'pattern': '[A-z]'}}"
                  v-maska="'Z#Z #Z#'"
                  @input="state.postalcode = state.postalcode.toUpperCase()"
                  :class="validityColor('postalcode')"
                  name="postalcode"
                />
                <div v-if="!$v.postalcode.required" class="invalid-tooltip">
                  This field is required
                </div>
                <div
                  v-if="
                    $v.postalcode.required && !$v.postalcode.postalCodeValidator
                  "
                  class="invalid-tooltip"
                >
                  Not a valid postal code
                </div>
              </div>
            </div>

            <div
              class="row mt-3"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_additional_sis_data
              "
            >
              <div
                class="form-group col"
                :class="{ required: $v.phone.hasOwnProperty('required') }"
              >
                <label>Phone Number</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="state.phone"
                  @blur="$v.phone.$touch()"
                  v-maska="'###-###-####'"
                  :class="validityColor('phone')"
                  name="phone"
                />
                <div v-if="!$v.phone.required" class="invalid-tooltip">
                  This field is required
                </div>
                <div
                  v-if="$v.phone.required && !$v.phone.phoneValidator"
                  class="invalid-tooltip"
                >
                  Not a valid phone number
                </div>
              </div>
            </div>

            <div
              class="row big-date mt-3"
              :class="{ required: $v.dob.hasOwnProperty('required') }"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_additional_sis_data
              "
            >
              <CDatePicker
                :class="validityColor('dob')"
                label="Date of Birth"
                @update:date="(date) => ($v.dob.$model = date)"
              ></CDatePicker>
            </div>

            <div
              class="row mt-3"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_yog
              "
            >
              <div
                class="form-group col"
                :class="{ required: $v.yog.hasOwnProperty('required') }"
              >
                <label>Year of Graduation</label>
                <BFormSelect
                  v-model="$v.yog.$model"
                  :class="validityColor('yog')"
                >
                  <option v-for="n in 10" :key="n">
                    {{ new Date().getFullYear() + (n - 2) }}
                  </option>
                </BFormSelect>
                <div class="invalid-tooltip">This field is required</div>
              </div>
            </div>

            <div class="row mt-3">
              <div
                class="form-group col"
                :class="{
                  required: $v.aboriginality.hasOwnProperty('required'),
                }"
              >
                <label>Aboriginal Status (voluntary)</label>
                <BFormSelect
                  v-model="$v.aboriginality.$model"
                  :options="state.aboriginalities"
                  :class="validityColor('aboriginality')"
                ></BFormSelect>
              </div>
            </div>
            <div
              class="row mt-3"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_citizenship
              "
            >
              <div
                class="form-group col"
                :class="{
                  required: $v.citizenship1.hasOwnProperty('required'),
                }"
              >
                <label>Citizenship 1</label>
                <v-select
                  v-model="$v.citizenship1.$model"
                  :options="state.countries"
                  :class="validityColor('citizenship1')"
                ></v-select>
                <div
                  v-if="$v.citizenship1.$error"
                  class="custom-invalid-tooltip"
                >
                  This field is required
                </div>
              </div>
              <div
                class="form-group col"
                :class="{
                  required: $v.citizenship2.hasOwnProperty('required'),
                }"
              >
                <label>Citizenship 2</label>
                <v-select
                  v-model="state.citizenship2"
                  :options="state.countries"
                  :class="validityColor('citizenship2')"
                ></v-select>
                <div
                  v-if="$v.citizenship2.$error"
                  class="custom-invalid-tooltip"
                >
                  This field is required
                </div>
              </div>
              <div
                v-show="
                  state.citizenship1 && state.citizenship1.label !== 'Canada'
                "
                class="form-group col"
                :class="{
                  required: $v.residentType.hasOwnProperty('required'),
                }"
              >
                <label>Resident Type</label>
                <v-select
                  v-model="state.residentType"
                  :options="state.residentTypes"
                  :class="validityColor('residentType')"
                ></v-select>
                <div
                  v-if="$v.residentType.$error"
                  class="custom-invalid-tooltip"
                >
                  This field is required
                </div>
              </div>
            </div>
            <div
              class="row mt-3"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_language
              "
            >
              <div
                class="form-group col"
                :class="{ required: $v.language1.hasOwnProperty('required') }"
              >
                <label>1st Language</label>
                <v-select
                  v-model="state.language1"
                  :options="state.languages"
                  :class="validityColor('language1')"
                ></v-select>
                <div v-if="$v.language1.$error" class="custom-invalid-tooltip">
                  This field is required
                </div>
              </div>
              <div
                class="form-group col"
                :class="{ required: $v.language2.hasOwnProperty('required') }"
              >
                <label>2nd Language</label>
                <v-select
                  v-model="state.language2"
                  :options="state.languages"
                  :class="validityColor('language2')"
                ></v-select>
                <div v-if="$v.language2.$error" class="custom-invalid-tooltip">
                  This field is required
                </div>
              </div>
            </div>
            <div
              class="row mt-3"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_origins
              "
            >
              <div
                class="form-group col"
                :class="{ required: $v.cob.hasOwnProperty('required') }"
              >
                <label>Country of Birth</label>
                <v-select
                  v-model="$v.cob.$model"
                  :options="state.countries"
                  :class="validityColor('cob')"
                ></v-select>
                <div v-if="$v.cob.$error" class="custom-invalid-tooltip">
                  This field is required
                </div>
              </div>
              <div
                class="form-group col"
                :class="{ required: $v.coo.hasOwnProperty('required') }"
              >
                <label>Country of Origin</label>
                <v-select
                  v-model="state.coo"
                  :options="state.countries"
                  :class="validityColor('coo')"
                ></v-select>
                <div v-if="$v.coo.$error" class="custom-invalid-tooltip">
                  This field is required
                </div>
              </div>
            </div>
            <div
              class="row mt-3"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_religion
              "
            >
              <div
                class="form-group col"
                :class="{
                  required: $v.studentReligion.hasOwnProperty('required'),
                }"
              >
                <label>Student's Religion</label>
                <v-select
                  v-model="$v.studentReligion.$model"
                  :options="state.religions"
                  :class="validityColor('studentReligion')"
                ></v-select>
                <div
                  v-if="$v.studentReligion.$error"
                  class="custom-invalid-tooltip"
                >
                  This field is required
                </div>
              </div>
              <div
                class="form-group col"
                :class="{
                  required: $v.motherReligion.hasOwnProperty('required'),
                }"
              >
                <label>Mother's Religion</label>
                <v-select
                  v-model="state.motherReligion"
                  :options="state.religions"
                  :class="validityColor('motherReligion')"
                ></v-select>
                <div
                  v-if="$v.motherReligion.$error"
                  class="custom-invalid-tooltip"
                >
                  This field is required
                </div>
              </div>
              <div
                class="form-group col"
                :class="{
                  required: $v.fatherReligion.hasOwnProperty('required'),
                }"
              >
                <label>Father's Religion</label>
                <v-select
                  v-model="state.fatherReligion"
                  :options="state.religions"
                  :class="validityColor('motherReligion')"
                ></v-select>
                <div
                  v-if="$v.fatherReligion.$error"
                  class="custom-invalid-tooltip"
                >
                  This field is required
                </div>
              </div>
            </div>

            <div class="row mt-3">
              <div
                class="form-group col"
                :class="{
                  required: $v.studentCell.hasOwnProperty('required'),
                }"
              >
                <label>Student's Cell</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="state.studentCell"
                  @blur="$v.studentCell.$touch()"
                  v-maska="'###-###-####'"
                  :class="validityColor('studentCell')"
                />
                <div
                  v-if="$v.studentCell.$model && !$v.studentCell.phoneValidator"
                  class="invalid-tooltip"
                >
                  Not a valid phone number
                </div>
              </div>
            </div>
            <div class="row mt-3">
              <div
                class="form-group col"
                :class="{
                  required: $v.studentEmail.hasOwnProperty('required'),
                }"
              >
                <label>Student Email</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="$v.studentEmail.$model"
                  :class="validityColor('studentEmail')"
                />
                <div v-if="!$v.studentEmail.required" class="invalid-tooltip">
                  This field is required
                </div>
                <div
                  v-if="$v.studentEmail.required && !$v.studentEmail.email"
                  class="invalid-tooltip"
                >
                  Not a valid email address
                </div>
                <div
                  v-if="
                    $v.studentEmail.required && !$v.studentEmail.confirmEmail
                  "
                  class="invalid-tooltip"
                >
                  Student and parent email address cannot be the same
                </div>
              </div>
            </div>

            <div class="row mt-3">
              <div
                class="form-group col"
                :class="{
                  required: $v.studentEmail2.hasOwnProperty('required'),
                }"
              >
                <label>Confirm Student Email</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="$v.studentEmail2.$model"
                  :class="validityColor('studentEmail2')"
                />
                <div v-if="!$v.studentEmail2.required" class="invalid-tooltip">
                  This field is required
                </div>
                <div
                  v-if="
                    $v.studentEmail2.required && !$v.studentEmail2.confirmEmail
                  "
                  class="invalid-tooltip"
                >
                  Email addresses do not match
                </div>
              </div>
            </div>

            <div
              class="row mt-3"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_additional_sis_data
              "
            >
              <div
                class="form-group col"
                :class="{
                  required: $v.ministryNo.hasOwnProperty('required'),
                }"
              >
                <label>Sask Learning ID</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="$v.ministryNo.$model"
                  :class="validityColor('ministryNo')"
                />
                <div v-if="!$v.ministryNo.required" class="invalid-tooltip">
                  This field is required
                </div>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col">
                <div class="form-group mb-0 required">
                  <label>Courses</label>
                </div>
                <div>
                  <BButton
                    variant="info"
                    class="text-white"
                    size="sm"
                    @click="
                      state.selectedCourses.push({
                        course: null,
                        startDate: null,
                        availableStartDates: [],
                        calendarAttrs: [],
                      })
                    "
                    ><i class="fa fa-plus"></i> Add a Course</BButton
                  >
                </div>
              </div>
            </div>
            <BRow
              v-for="(selection, index) in state.selectedCourses"
              :key="index"
            >
              <BCol class="mt-4">
                <BFormGroup label="Course">
                  <v-select
                    v-model="state.selectedCourses[index].course"
                    :options="selectableCourses"
                    @option:selected="changeCourseInput(index)"
                  ></v-select>
                </BFormGroup>
                <div class="text-center">
                  <BButton
                    variant="danger"
                    class="my-2 btn-sm text-white"
                    @click="removeSelectedCourse(index)"
                    >Remove Course</BButton
                  >
                </div>
                <div
                  v-if="state.selectedCourses[index].course"
                  class="text-center"
                >
                  This course is
                  <b>{{ state.selectedCourses[index].course.length }}</b>
                  days long.<br /><br />
                </div>
                <div
                  v-if="
                    state.selectedCourses[index].course &&
                    state.selectedCourses[index].startDate
                  "
                  class="text-center"
                >
                  You have chosen a start date of <br /><b>
                    {{
                      dateFormat(
                        new Date(state.selectedCourses[index].startDate),
                        "mmmm dd, yyyy"
                      )
                    }}</b
                  >.<br /><br />
                  <span v-if="state.selectedCourses[index].willBeSuspended">
                    You will complete
                    <b>{{
                      state.selectedCourses[index]
                        .amountCompleteBeforeSuspension
                    }}</b>
                    days of your course before it must be suspended for summer
                    at midnight on
                    <b>
                      {{
                        dateFormat(
                          new Date(
                            state.selectedCourses[index].semesterEndDate
                          ),
                          "mmmm dd, yyyy"
                        )
                      }}</b
                    >. You will have
                    <b>{{
                      state.selectedCourses[index].course?.length -
                      1 -
                      state.selectedCourses[index]
                        .amountCompleteBeforeSuspension
                    }}</b>
                    days remaining when your course resumes in September.
                  </span>
                  <span v-else>
                    You will have access to your course until midnight on
                    <br /><b>{{
                      dateFormat(
                        addDays(
                          new Date(state.selectedCourses[index].startDate),
                          state.selectedCourses[index].course?.length - 1
                        ),
                        "mmmm dd, yyyy"
                      )
                    }}</b
                    ><br />Final assessments will be scheduled prior to this
                    date.
                  </span>
                </div>
              </BCol>
              <BCol class="mt-3 w-100 text-center">
                <BFormGroup label="Dates" v-if="selectedCourses[index]?.course">
                  <VDatePicker
                    :id="'calendarDiv' + index"
                    v-model="selectedCourses[index].startDate"
                    popover-visibility="focus"
                    :attributes="selectedCourses[index].calendarAttrs"
                    :inline="true"
                    :expanded="true"
                    :double-paned="true"
                    :linked="true"
                    @dayclick="
                      (calendarDay, event) => pickDate(calendarDay, index)
                    "
                  >
                  </VDatePicker>
                </BFormGroup>
                <div class="mx-auto fw-bold" v-else>
                  Select a course to see potential dates
                </div>
              </BCol>
              <BTooltip
                v-if="!state.selectedCourses[index].course"
                :target="'calendarDiv' + index"
                title="Please select a course from the dropdown to see available dates"
              ></BTooltip>
            </BRow>
            <div
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_parent
              "
            >
              <div class="row mt-3">
                <div
                  class="form-group col"
                  :class="{
                    required: $v.parentFirstName.hasOwnProperty('required'),
                  }"
                >
                  <label>Parent/Guardian First Name</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="$v.parentFirstName.$model"
                    :class="validityColor('parentFirstName')"
                  />
                  <div
                    v-if="!$v.parentFirstName.required"
                    class="invalid-tooltip"
                  >
                    This field is required
                  </div>
                </div>
                <div
                  class="form-group col"
                  :class="{
                    required: $v.parentLastName.hasOwnProperty('required'),
                  }"
                >
                  <label>Parent/Guardian Last Name</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="$v.parentLastName.$model"
                    :class="validityColor('parentLastName')"
                  />
                  <div
                    v-if="!$v.parentLastName.required"
                    class="invalid-tooltip"
                  >
                    This field is required
                  </div>
                </div>
              </div>

              <div class="row mt-3">
                <div
                  class="form-group col"
                  :class="{
                    required: $v.parentEmail.hasOwnProperty('required'),
                  }"
                >
                  <label>Parent/Guardian Email</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="$v.parentEmail.$model"
                    :class="validityColor('parentEmail')"
                  />
                  <div v-if="!$v.parentEmail.required" class="invalid-tooltip">
                    This field is required
                  </div>
                  <div
                    v-if="$v.parentEmail.required && !$v.parentEmail.email"
                    class="invalid-tooltip"
                  >
                    Not a valid email address
                  </div>
                </div>
              </div>

              <div class="row mt-3">
                <div
                  class="form-group col"
                  :class="{
                    required: $v.parentEmail2.hasOwnProperty('required'),
                  }"
                >
                  <label>Confirm Parent/Guardian Email</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="$v.parentEmail2.$model"
                    :class="validityColor('parentEmail2')"
                  />
                  <div v-if="!$v.parentEmail2.required" class="invalid-tooltip">
                    This field is required
                  </div>
                  <div
                    v-if="
                      $v.parentEmail2.required && !$v.parentEmail2.confirmEmail
                    "
                    class="invalid-tooltip"
                  >
                    Email addresses do not match
                  </div>
                </div>
              </div>

              <div class="row mt-3">
                <div
                  class="form-group col"
                  :class="{
                    required: $v.parentPhone.hasOwnProperty('required'),
                  }"
                >
                  <label>Parent/Guardian Phone</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="state.parentPhone"
                    @blur="$v.parentPhone.$touch()"
                    v-maska="'###-###-####'"
                    :class="validityColor('parentPhone')"
                  />
                  <div v-if="!$v.parentPhone.required" class="invalid-tooltip">
                    This field is required
                  </div>
                  <div
                    v-if="
                      $v.parentPhone.required && !$v.parentPhone.phoneValidator
                    "
                    class="invalid-tooltip"
                  >
                    Not a valid phone number
                  </div>
                </div>
              </div>
            </div>

            <div
              class="row mt-3"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_school_division
              "
            >
              <div
                class="form-group col"
                :class="{
                  required: $v.schoolDivision.hasOwnProperty('required'),
                }"
              >
                <label>School Division</label>
                <BFormSelect
                  v-model="$v.schoolDivision.$model"
                  :options="[...state.schoolDivisions, 'Other']"
                  :class="validityColor('schoolDivision')"
                ></BFormSelect>
                <div v-if="!$v.schoolDivision.required" class="invalid-tooltip">
                  This field is required
                </div>
              </div>
              <div
                v-show="$v.schoolDivision.$model == 'Other'"
                class="form-group col"
                :class="{
                  required: $v.schoolDivisionOther.hasOwnProperty('required'),
                }"
              >
                <label>School Division</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="$v.schoolDivisionOther.$model"
                  :class="validityColor('schoolDivisionOther')"
                />
                <div
                  v-if="!$v.schoolDivisionOther.required"
                  class="invalid-tooltip"
                >
                  This field is required
                </div>
              </div>
            </div>

            <div
              class="row mt-3"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_school
              "
            >
              <div
                class="form-group col"
                :class="{ required: $v.school.hasOwnProperty('required') }"
              >
                <label>School</label>
                <BFormSelect
                  v-model="$v.school.$model"
                  :options="[...state.schools, 'Other']"
                  :class="validityColor('school')"
                ></BFormSelect>
                <div v-if="!$v.school.required" class="invalid-tooltip">
                  This field is required
                </div>
              </div>
              <div
                v-show="$v.school.$model == 'Other'"
                class="form-group col"
                :class="{
                  required: $v.schoolOther.hasOwnProperty('required'),
                }"
              >
                <label>School</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="$v.schoolOther.$model"
                  :class="validityColor('schoolOther')"
                />
                <div v-if="!$v.schoolOther.required" class="invalid-tooltip">
                  This field is required
                </div>
              </div>
            </div>

            <div
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_school_contact
              "
            >
              <div class="row mt-3">
                <div
                  class="form-group col"
                  :class="{
                    required:
                      $v.schoolContactFirstName.hasOwnProperty('required'),
                  }"
                >
                  <label>School Contact First Name</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="$v.schoolContactFirstName.$model"
                    :class="validityColor('schoolContactFirstName')"
                  />
                  <div
                    v-if="!$v.schoolContactFirstName.required"
                    class="invalid-tooltip"
                  >
                    This field is required
                  </div>
                </div>
                <div
                  class="form-group col"
                  :class="{
                    required:
                      $v.schoolContactLastName.hasOwnProperty('required'),
                  }"
                >
                  <label>School Contact Last Name</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="$v.schoolContactLastName.$model"
                    :class="validityColor('schoolContactLastName')"
                  />
                  <div
                    v-if="!$v.schoolContactLastName.required"
                    class="invalid-tooltip"
                  >
                    This field is required
                  </div>
                </div>
              </div>

              <div class="row mt-3">
                <div
                  class="form-group col"
                  :class="{
                    required: $v.schoolContactEmail.hasOwnProperty('required'),
                  }"
                >
                  <label>School Contact Email</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="$v.schoolContactEmail.$model"
                    :class="validityColor('schoolContactEmail')"
                  />
                  <div
                    v-if="!$v.schoolContactEmail.required"
                    class="invalid-tooltip"
                  >
                    This field is required
                  </div>
                  <div
                    v-if="
                      $v.schoolContactEmail.required &&
                      !$v.schoolContactEmail.email
                    "
                    class="invalid-tooltip"
                  >
                    Not a valid email address
                  </div>
                </div>
              </div>
            </div>

            <div
              class="row px-1 py-2 mt-3 mx-0"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_attending_other_school_div
              "
              style="border: 1px solid #e4e7ea; border-radius: 0.25em"
            >
              <div
                class="col-12"
                :class="{
                  required:
                    $v.attendingOtherSchoolDivision.hasOwnProperty('required'),
                }"
              >
                <label
                  ><b
                    >Is this student attending classes at another school
                    division?</b
                  ></label
                >
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="exampleRadios"
                    id="exampleRadios1"
                    value="true"
                    v-model="state.attendingOtherSchoolDivision"
                    :class="validityColor('attendingOtherSchoolDivision')"
                  />
                  <label class="form-check-label" for="exampleRadios1">
                    Yes (Please provide details in the Additional Information
                    section below)
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="exampleRadios"
                    id="exampleRadios2"
                    value="false"
                    v-model="state.attendingOtherSchoolDivision"
                    :class="validityColor('attendingOtherSchoolDivision')"
                  />
                  <label class="form-check-label" for="exampleRadios2">
                    No
                  </label>
                </div>
                <div
                  v-if="
                    !$v.attendingOtherSchoolDivision.required &&
                    $v.attendingOtherSchoolDivision.$dirty
                  "
                  class="custom-invalid-tooltip"
                  style="right: -8px; margin-top: 10px"
                >
                  This field is required
                </div>
              </div>

              <div
                class="col-12 mt-4"
                v-show="
                  state.studentType &&
                  state.studentTypes.filter((a) => a.id == state.studentType)[0]
                    .require_attending_other_school_div &&
                  state.attendingOtherSchoolDivision == 'false'
                "
              >
                <div
                  class="form-group col"
                  :class="{
                    required:
                      $v.lastAttendedSchoolDivision.hasOwnProperty('required'),
                  }"
                >
                  <label>Last Attended School Division</label>
                  <BFormSelect
                    v-model="$v.lastAttendedSchoolDivision.$model"
                    :options="[...state.schoolDivisions, 'Other']"
                    :class="validityColor('lastAttendedSchoolDivision')"
                  ></BFormSelect>
                  <div
                    v-if="!$v.lastAttendedSchoolDivision.required"
                    class="invalid-tooltip"
                  >
                    This field is required
                  </div>
                </div>
                <div
                  v-show="$v.lastAttendedSchoolDivision.$model == 'Other'"
                  class="form-group col mt-3"
                  :class="{
                    required:
                      $v.lastAttendedSchoolDivisionOther.hasOwnProperty(
                        'required'
                      ),
                  }"
                >
                  <label>Last Attended School Division</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="$v.lastAttendedSchoolDivisionOther.$model"
                    :class="validityColor('lastAttendedSchoolDivisionOther')"
                  />
                  <div
                    v-if="!$v.lastAttendedSchoolDivisionOther.required"
                    class="invalid-tooltip"
                  >
                    This field is required
                  </div>
                </div>
              </div>

              <div
                class="col-12 mt-3"
                v-show="
                  state.studentType &&
                  state.studentTypes.filter((a) => a.id == state.studentType)[0]
                    .require_attending_other_school_div &&
                  state.attendingOtherSchoolDivision == 'false'
                "
              >
                <div
                  class="form-group col"
                  :class="{
                    required: $v.lastAttendedSchool.hasOwnProperty('required'),
                  }"
                >
                  <label>Last Attended School</label>
                  <BFormSelect
                    v-model="$v.lastAttendedSchool.$model"
                    :options="[...state.schools, 'Other']"
                    :class="validityColor('lastAttendedSchool')"
                  ></BFormSelect>
                  <div
                    v-if="!$v.lastAttendedSchool.required"
                    class="invalid-tooltip"
                  >
                    This field is required
                  </div>
                </div>
                <div
                  v-show="$v.lastAttendedSchool.$model == 'Other'"
                  class="form-group col"
                  :class="{
                    required:
                      $v.lastAttendedSchoolOther.hasOwnProperty('required'),
                  }"
                >
                  <label>Last Attended School</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="$v.lastAttendedSchoolOther.$model"
                    :class="validityColor('lastAttendedSchoolOther')"
                  />
                  <div
                    v-if="!$v.lastAttendedSchoolOther.required"
                    class="invalid-tooltip"
                  >
                    This field is required
                  </div>
                </div>
              </div>

              <div
                class="col-12 big-date mt-3 mb-2"
                :class="{
                  required: $v.lastAttendedDate.hasOwnProperty('required'),
                }"
                v-show="
                  state.studentType &&
                  state.studentTypes.filter((a) => a.id == state.studentType)[0]
                    .require_attending_other_school_div &&
                  state.attendingOtherSchoolDivision == 'false'
                "
              >
                <CDatePicker
                  :class="validityColor('lastAttendedDate')"
                  class="mb-0"
                  label="Date of Withdrawal"
                  @update:date="(date) => ($v.lastAttendedDate.$model = date)"
                ></CDatePicker>
              </div>
            </div>

            <div
              class="row mt-3"
              v-show="
                state.studentType &&
                state.studentTypes.filter((a) => a.id == state.studentType)[0]
                  .require_partner_in_education &&
                state.educationPartners.length > 0
              "
            >
              <div
                class="form-group col"
                :class="{
                  required: $v.partnerInEducation.hasOwnProperty('required'),
                }"
              >
                <label
                  >Partner in Education<br /><small
                    >Are you a member of one of these partner
                    organizations?</small
                  ></label
                >
                <BFormSelect
                  v-model="$v.partnerInEducation.$model"
                  :options="state.educationPartners"
                  :class="validityColor('partnerInEducation')"
                ></BFormSelect>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col">
                <b>Tuition Fees and Rules</b><br />
                <ul>
                  <li>
                    Course Tuition (Regular School Year) - $500 per course
                    <ul>
                      <li>
                        Tuition is waived for RCSD students and students who are
                        21 years or younger on September 30 of the school year
                        the course is completed and are not enrolled with
                        another school division.
                      </li>
                      <li>
                        International Student Course Tuition - $1300 per course
                      </li>
                    </ul>
                  </li>
                  <li>
                    Summer Course Tuition - $500 per course
                    <ul>
                      <li>
                        International Student Summer Course Tuition - $1000 per
                        course
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
            <br />
            <div class="row mt-3">
              <div
                class="form-group col"
                :class="{ required: $v.notes.hasOwnProperty('required') }"
              >
                <label class="mb-0"
                  >Reason for Application and/or Additional Information</label
                >

                <br /><small
                  >This information will be available to Learning Online
                  administration and/or base school administration for
                  consideration during application review.</small
                >
                <div v-if="$v.notes.$error" class="custom-invalid-tooltip my-2">
                  This field is required
                </div>
                <BFormTextarea
                  id="textarea1"
                  v-model="$v.notes.$model"
                  placeholder=""
                  :rows="3"
                  :max-rows="6"
                  class="mt-1"
                  :class="{ 'border-danger': $v.notes.$error }"
                >
                </BFormTextarea>
              </div>
            </div>
            <div class="row mt-3">
              <div class="form-group col">
                <BFormCheckbox
                  v-model="$v.eligibilityGuidelines.$model"
                  class="required"
                  >Eligibility Guidelines were considered while processing
                  registration</BFormCheckbox
                >
                <BFormCheckbox v-model="$v.prereqs.$model" class="required"
                  >Pre-requisite courses, if required, have been
                  completed</BFormCheckbox
                >
                <BFormCheckbox v-model="$v.accurate.$model" class="required"
                  >Information provided is accurate and truthful</BFormCheckbox
                >
                <BFormCheckbox
                  v-model="$v.recordsShared.$model"
                  class="required"
                  >I am aware that educational records and applicable
                  information will be shared with and accessed from previous
                  and/or current schools</BFormCheckbox
                >
                <div
                  v-if="
                    $v.eligibilityGuidelines.$error ||
                    $v.prereqs.$error ||
                    $v.accurate.$error ||
                    $v.recordsShared.$error
                  "
                  class="custom-invalid-tooltip mb-2"
                >
                  Please indicate your agreement to all of the above
                </div>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col">
                <vue-recaptcha
                  v-if="state.loadRecaptcha"
                  ref="invisibleRecaptcha"
                  @expire="onExpired"
                  @verify="recaptchaCallback"
                  sitekey="6LdZ730UAAAAAGUsCZJW6MlDv7blUDBW630j83XE"
                  size="invisible"
                >
                </vue-recaptcha>

                <h5 class="text-center mb-4">
                  Student registration is dependent upon approval from the base
                  school administration.
                </h5>
                <div class="d-grid gap-2">
                  <BButton
                    type="button"
                    block
                    variant="primary"
                    @click="verifyData"
                    :disabled="!state.buttonEnabled"
                    ><span>Submit Registration</span></BButton
                  >
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="row mt-3">
              <div class="col text-center">
                <i
                  class="fa fa-check-circle fa-5x text-success"
                  aria-hidden="true"
                ></i
                ><br />
                <h4 class="mt-2 mb-3">Your registration has been received.</h4>
                <p>
                  Once your placement has been reviewed and approved you will
                  receive an email at {{ state.studentEmail }} where you will be
                  provided instructions on your next steps.
                </p>
                <p
                  v-if="
                    state.studentType &&
                    state.studentTypes.filter(
                      (a) => a.id == state.studentType
                    )[0].require_parent
                  "
                >
                  Additionally, {{ state.parentEmail }} will also be receiving
                  an email with additional steps required.
                </p>
                <BButton
                  variant="warning"
                  class="ml-2 text-white"
                  @click="resetForm"
                  >Reset Form</BButton
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <LoadingModal :visible="showLoading"></LoadingModal>

    <ErrorModal :visible="showError"></ErrorModal>

  </div>
</template>
<style>
body {
  background-color: #e4e6e4;
}

label {
  width: "100%";
}

.big-date > .form-label {
  margin-bottom: 0px;
}
</style>
